import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import FieldNames from "../defaults/FieldNames.json";
import { getAppConfig, getAttributeServiceUrl } from "../utils/ReduxHelper";

// This service is intended for getting the sequential ID field when creating an entity when the service returns the objectID in its response
const IDService = {
  getActionID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.actionAttributes[config.env].layerId, objectID);
  },
  getActionObjectID: function (
    sequentialID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDBySequentialID(rootServiceUrl + "/" + config.actionAttributes[config.env].layerId, sequentialID);
  },
  getActionJoinValues: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getJoinValuesByObjectID(rootServiceUrl + "/" + config.actionAttributes[config.env].layerId, objectID);
  },
  getGeometryID: function (
    objectID,
    geometryType
  ) {
    if(geometryType === FieldNames.joinFeatureValues.point)
    {
      return this.getPointID(objectID);
    }
    else if(geometryType === FieldNames.joinFeatureValues.line)
    {
      return this.getLineID(objectID);
    }
    else // Default to Polygon
    {
      return this.getPolygonID(objectID);
    }
  },
  getGeometryOBJECTID: function (
    ID,
    geometryType
  ) {
    if(geometryType === FieldNames.joinFeatureValues.point)
    {
      return this.getPointOBJECTID(ID);
    }
    else if(geometryType === FieldNames.joinFeatureValues.line)
    {
      return this.getLineOBJECTID(ID);
    }
    else // Default to Polygon
    {
      return this.getPolygonOBJECTID(ID);
    }
  },
  getPolygonID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.ifprsPolygons[config.env].layerId, objectID);
  },
  getPointID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.ifprsPoints[config.env].layerId, objectID);
  },
  getLineID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.ifprsLines[config.env].layerId, objectID);
  },
  getPolygonOBJECTID: function (
    ID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDByID(rootServiceUrl + "/" + config.ifprsPolygons[config.env].layerId, ID);
  },
  getPointOBJECTID: function (
    ID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDByID(rootServiceUrl + "/" + config.ifprsPoints[config.env].layerId, ID);
  },
  getLineOBJECTID: function (
    ID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDByID(rootServiceUrl + "/" + config.ifprsLines[config.env].layerId, ID);
  },
  getUserID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.users[config.env].layerId, objectID);
  },
  getUserObjectID: function (
    sequentialID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDBySequentialID(rootServiceUrl + "/" + config.users[config.env].layerId, sequentialID);
  },
  getBudgetID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.budget[config.env].layerId, objectID);
  },
  getProjectID: function (
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.ifprsProjects[config.env].layerId, objectID);
  },
  getGroupID: function(
    objectID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getIDByObjectID(rootServiceUrl + "/" + config.treatmentActivityGroup[config.env].layerId, objectID);
  },
  getBudgetObjectID: function (
    sequentialID
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getObjectIDBySequentialID(rootServiceUrl + "/" + config.budget[config.env].layerId, sequentialID);
  },
  getMultipleBudgetIDs: function (
    objectIDArr
  ) {
    const config = getAppConfig();
    const rootServiceUrl = getAttributeServiceUrl();
    return this.getMultipleIDsByObjectID(rootServiceUrl + "/" + config.budget[config.env].layerId, objectIDArr);
  },

  // Generic Querying Methods
  getObjectIDByID: function (
    fullUrl,
    ID
  ) {
    try
    {
      const queryObject = new Query({
          where: "(ID = " + ID + ")",
          outFields: "OBJECTID",
          returnGeometry: false,
      });

      return query.executeQueryJSON(fullUrl, queryObject).then(results => {
        let idObj = {};
        if(results.features && results.features.length > 0) {
          idObj = results.features.map((eaItem) => eaItem.attributes)[0];
        } 
        return idObj?.[FieldNames.featureFields.objectID];
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getIDByObjectID: function (
    fullUrl,
    objectID
  ) {
    try
    {
      const queryObject = new Query({
          where: "(ObjectID = " + objectID + ")",
          outFields: "ID",
          returnGeometry: false,
      });

      return query.executeQueryJSON(fullUrl, queryObject).then(results => {
        let idObj = {};
        if(results.features && results.features.length > 0) {
          idObj = results.features.map((eaItem) => eaItem.attributes)[0];
        } 
        return idObj?.[FieldNames.featureFields.id];
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getJoinValuesByObjectID: function (
    fullUrl,
    objectID
  ) {
    try
    {
      const queryObject = new Query({
          where: "(ObjectID = " + objectID + ")",
          outFields: "JoinID, JoinFeature",
          returnGeometry: false,
      });

      return query.executeQueryJSON(fullUrl, queryObject).then(results => {
        let idObj = {};
        if(results.features && results.features.length > 0) {
          idObj = results.features.map((eaItem) => eaItem.attributes)[0];
        } 
        return idObj?.[FieldNames.featureFields.id];
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getMultipleIDsByObjectID: function (
    fullUrl,
    objectIDArr
  ) {
    try
    {
      const queryObject = new Query({
          where: "(ObjectID IN (" + objectIDArr.join(", ") + "))",
          outFields: "OBJECTID, ID",
          returnGeometry: false,
      });

      return query.executeQueryJSON(fullUrl, queryObject).then(results => {
        let idObjArr = {};
        if(results.features && results.features.length > 0) {
          idObjArr = results.features.map((eaItem) => eaItem.attributes);
        }
        // OBJECTID and ID are both returned so there is a relation to match IDs
        return idObjArr;
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getObjectIDBySequentialID: function (
    fullUrl,
    ID
  ) {
    try
    {
      const queryObject = new Query({
          where: "(ID = " + ID + ")",
          outFields: "OBJECTID",
          returnGeometry: false,
      });

      return query.executeQueryJSON(fullUrl, queryObject).then(results => {
        let idObj = {};
        if(results.features && results.features.length > 0) {
          idObj = results.features.map((eaItem) => eaItem.attributes)[0];
        } 
        return idObj?.[FieldNames.featureFields.objectID];
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default IDService;
